import type { z } from 'zod/v4';
import type { TGeneric, TGenericAny, THTTPMethod, PathParams } from '@epihub/core/src/server';

/**
 * This type serves as the core definition of a procedure.
 * All parameters that are needed to
 *  1. Send a fetch of that procedure
 *  2. Handle a fetch of that procedure
 * Must be defined here
 */
export type Procedure<
  Path extends string = string,
  Method extends THTTPMethod = THTTPMethod,
  Query extends Record<string, TGenericAny> = TGenericAny,
  Headers extends Record<string, string> = TGenericAny,
  Responses extends Record<number, TGeneric> = Record<number, TGenericAny>,
  Body extends object = {},
  Params extends {
    [K in keyof PathParams<Path>]: TGenericAny;
  } = {
    [K in keyof PathParams<Path>]: TGenericAny;
  },
> = {
  path: Path;
  params: z.ZodType<Params>;

  method: Method;
  query: z.ZodType<Query>;
  headers: z.ZodType<Headers>;

  responses: {
    [K in keyof Responses]: z.ZodType<Responses[K]>;
  };

  body: z.ZodType<Body>;
};

export function procedure<
  Path extends string = string,
  Method extends THTTPMethod = THTTPMethod,
  Query extends Record<string, TGenericAny> = TGenericAny,
  Headers extends Record<string, string> = TGenericAny,
  Responses extends Record<number, TGeneric> = Record<number, TGenericAny>,
  Body extends object = {},
>(
  def: Procedure<
    Path,
    Method,
    Query,
    Headers,
    Responses,
    Body
  >,
) {
  return def as any as Procedure<
    Path,
    Method,
    Query,
    Headers,
    Responses,
    Body
  >;
}
